/**
 * useAuditItemMetadata Hook
 *
 * Custom hook to extract metadata logic from AuditItemContent component
 * Reduces component complexity and improves maintainability.
 */

import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { useMemo } from 'react';

export function useAuditItemMetadata(item: GraphQLAuditItem) {
  // Determine if the item is safe content using classificationType field
  // Aligned with classification testing approach - explicit check for safe content
  const isSafeContent = useMemo(() => {
    const classificationType = item.contentSnapshot?.classificationType;

    // Strict validation: content is safe only if explicitly marked as 'safe_content'
    return classificationType === 'safe_content';
  }, [item.contentSnapshot?.classificationType]);

  // Get topic directly from contentSnapshot
  const topic = useMemo(() => item.contentSnapshot?.topic || 'Unknown', [item.contentSnapshot?.topic]);

  // Get content type directly from contentSnapshot
  const contentType = useMemo(() => item.contentSnapshot?.contentType?.toUpperCase() || 'UNKNOWN', [item.contentSnapshot?.contentType]);

  const contentUrl = item.contentSnapshot?.contentUrl || item.contentSnapshot?.audioFileUrl;

  // Check if audio player should be shown
  const shouldShowAudioPlayer = useMemo(() => contentUrl && contentType.toLowerCase() === 'audio', [contentUrl, contentType]);

  // Check if image viewer should be shown
  const shouldShowImageViewer = useMemo(() => contentUrl && contentType.toLowerCase() === 'image', [contentUrl, contentType]);

  return {
    isSafeContent,
    topic,
    contentType,
    contentUrl,
    shouldShowAudioPlayer,
    shouldShowImageViewer,
  };
}
